import React from 'react';
import { useLoaderData, Await, Navigate } from 'react-router-dom';
import { TextLink } from '@oysterjs/ui/text';

import {
  Action,
  CreateHtmlDocumentTemplateMutation,
  HtmlDocumentTemplateFieldsFragment,
  Resource
} from '~/api/graphql';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import { PermissionButton } from '~/components/permissions';

import { HtmlEditorPage } from '~/components/library/html-editor-page';
import { HtmlTemplateListPage } from '~/components/library/html-template-list-page';
import { LibraryEditOverlayLoader } from '~/components/library/library-edit-overlay-loader';
import { useActionValidationErrors } from '~/components/library/hooks/use-action-validation-errors';

const CREATE_STYLE = [{ resource: Resource.HtmlDocumentTemplate, action: Action.Create }];

/**
 * Styles tab of the library. Lists every HTML document template that has no
 * body (styles), with bulk archive / visibility flows shared with the
 * templates tab.
 */
export const LibraryStylesPage = () => {
  // Designs replace Styles: existing ones still open and save, but no new ones are made.
  const designsOn = useFlag(FEATURE_FLAGS.AGENCY_BRAND_DESIGN);
  return (
    <HtmlTemplateListPage
      kind="styles"
      calloutDescription={designsOn ? <StylesMovingDescription /> : undefined}
      extraActions={
        !designsOn && (
          <PermissionButton
            permissions={CREATE_STYLE}
            whenDenied="hide"
            color="sky"
            href="/app/library/styles/new"
            className="min-h-12"
          >
            Create Style
          </PermissionButton>
        )
      }
    />
  );
};

const StylesMovingDescription = () => (
  <>
    Styles are moving to <TextLink href="/app/settings/design">Settings › Design</TextLink>, where
    new looks are made, and existing Styles keep working.
  </>
);

/** Create flow for a new pure style (HTML template without a body). */
export const StyleCreatePage = () => {
  const validationErrors = useActionValidationErrors<CreateHtmlDocumentTemplateMutation>();
  const designsOn = useFlag(FEATURE_FLAGS.AGENCY_BRAND_DESIGN);
  if (designsOn) {
    return <Navigate to="/app/library/styles" replace />;
  }
  return (
    <HtmlEditorPage
      kind="style"
      editMode={false}
      initialForm={{ title: '', description: '', baseStyleId: 'builtin:classic-professional' }}
      validationErrors={validationErrors}
      listUrl="/app/library/styles"
    />
  );
};

/** Edit flow for an existing style; the loader resolves the template. */
export const StyleEditPage = () => {
  const loader = useLoaderData() as
    | { data: Promise<{ htmlDocumentTemplate?: HtmlDocumentTemplateFieldsFragment }> }
    | undefined;
  const validationErrors = useActionValidationErrors<CreateHtmlDocumentTemplateMutation>();

  return (
    <React.Suspense fallback={<LibraryEditOverlayLoader />}>
      <Await resolve={loader?.data}>
        {(data: { htmlDocumentTemplate?: HtmlDocumentTemplateFieldsFragment } | undefined) => {
          const template = data?.htmlDocumentTemplate;
          if (!template) return null;
          return (
            <HtmlEditorPage
              kind="style"
              editMode
              initialTemplate={template}
              initialForm={{
                title: template.title,
                description: template.description ?? '',
                baseStyleId: template.baseStyleId ?? '',
                prefixFileId: template.prefixFiles?.[0]?.id ?? null,
                suffixFileId: template.suffixFiles?.[0]?.id ?? null,
                bodyTemplate: template.bodyTemplate ?? ''
              }}
              validationErrors={validationErrors}
              listUrl="/app/library/styles"
            />
          );
        }}
      </Await>
    </React.Suspense>
  );
};
